import React, { useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES, ROUTE_PATHS } from "../../../../constants/app.constants";
import { mockDistributorsData } from "../../distributors-table/mock.data";
import AddMemberIcon from "../../../icons/add-member.icon";
import ImageLoader from "../../../Common/ImageLoader";
import styles from "./msp-account-info-header.module.scss";

// Mock data - in real app this would come from API
const mockMspAccountsData = [
  {
    id: "1",
    companyName: "M3Networks ltd.",
    companyAvatar: null,
    subscriptionStatus: {
      label: "Paid Plan 1",
    },
    accountManager: {
      name: "Robin Smit",
    },
  },
  {
    id: "2",
    companyName: "ABC",
    companyAvatar: null,
    subscriptionStatus: {
      label: "Paid Plan 1",
    },
    accountManager: {
      name: "Robin Smit",
    },
  },
];

export const MspAccountInfoHeader = ({ isDistributorMspAccountInfo }) => {
  const { distributorId, mspAccountId: mspAccountIdFromParams } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract mspAccountId from pathname if not available from params
  const mspAccountId = useMemo(() => {
    if (mspAccountIdFromParams) return mspAccountIdFromParams;
    
    // Extract from pathname: /subscription-portal/distributors/:distributorId/msp-accounts/:mspAccountId/overview
    const match = location.pathname.match(/\/msp-accounts\/([^/]+)\/overview/);
    return match ? match[1] : null;
  }, [mspAccountIdFromParams, location.pathname]);

  const mspAccountData = useMemo(() => {
    if (!mspAccountId) {
      // Return default data if mspAccountId is not found
      return {
        id: mspAccountId || "unknown",
        companyName: "MSP Account",
        companyAvatar: null,
        subscriptionStatus: {
          label: "Paid Plan 1",
        },
        accountManager: {
          name: "Robin Smit",
        },
      };
    }
    return mockMspAccountsData.find(account => account.id === mspAccountId) || {
      id: mspAccountId,
      companyName: `MSP Account ${mspAccountId}`,
      companyAvatar: null,
      subscriptionStatus: {
        label: "Paid Plan 1",
      },
      accountManager: {
        name: "Robin Smit",
      },
    };
  }, [mspAccountId]);

  // Get distributor data for breadcrumb
  const distributorData = useMemo(() => {
    if (!distributorId) return null;
    return mockDistributorsData.find(d => d.id === distributorId);
  }, [distributorId]);

  if (!isDistributorMspAccountInfo) {
    return null;
  }

  const companyInitial = mspAccountData.companyName?.charAt(0).toUpperCase() || "";

  const handleBackToDistributors = () => {
    navigate(ROUTE_PATHS.subscriptionPortalPages.distributors);
  };

  const handleBackToMspAccounts = () => {
    if (distributorId) {
      navigate(ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccounts(distributorId));
    }
  };

  return (
    <>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbContainer}>
        <div className={styles.breadcrumbContent}>
          <div 
            className={styles.backButton}
            onClick={handleBackToDistributors}
          >
            <img
              src="/images/attack-surface/back-icon.svg"
              alt=""
              className={styles.backIcon}
            />
            <span className={styles.backText}>Back</span>
          </div>
          <span className={styles.breadcrumbSeparator}>Distributors</span>
          <span className={styles.breadcrumbDivider}>&nbsp; &nbsp;/&nbsp;&nbsp;</span>
          <span 
            className={styles.breadcrumbLink}
            onClick={handleBackToMspAccounts}
          >
            {distributorData?.company.name || "Distributor"}
          </span>
          <span className={styles.breadcrumbDivider}>&nbsp; &nbsp;/&nbsp;&nbsp;</span>
          <span className={styles.breadcrumbSeparator}>msp</span>
          <span className={styles.breadcrumbDivider}>&nbsp; &nbsp;/&nbsp;&nbsp;</span>
          <span className={styles.breadcrumbCurrent}>
            {mspAccountData.companyName}
          </span>
        </div>
      </div>

      {/* Header Content */}
      <div className={styles.container}>
      <ImageLoader
        src={mspAccountData.companyAvatar}
        alt={`${mspAccountData.companyName} avatar`}
        className={styles.avatar}
        noImageComponent={
          <div className={styles.avatarPlaceholder}>
            {companyInitial}
          </div>
        }
      />

      <div className={styles.content}>
        <div className={styles.infoWrapper}>
          <div className={styles.titleRow}>
            <div className={styles.title}>
              {mspAccountData.companyName}
            </div>

            <div className={styles.badge}>
              <div className={styles.badgeInner}>
                <div className={styles.badgeText}>
                  {mspAccountData.subscriptionStatus?.label || "Paid Plan 1"}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.contactRow}>
            <AddMemberIcon className={styles.icon} />
            <p className={styles.contactText}>
              <span className={styles.contactLabel}>Sales Rep:</span>
              <span className={styles.contactName}>
                {" "}
                {mspAccountData.accountManager?.name || "Robin Smit"}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
